import {
  decodeDominoFrames,
  type DominoReplay,
  type DominoResponse,
  type DominoAction,
  type DominoContact,
} from './Domino.js';

function object(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== 'object') throw new Error('Invalid domino response');
  return value as Record<string, unknown>;
}
export function parseDominoResponse(value: unknown): DominoResponse {
  const root = object(value);
  if (typeof root.revision !== 'string' || root.revision.length > 160)
    throw new Error('Invalid domino revision');
  if (root.replay === undefined) return { revision: root.revision };
  if (root.replay === null) return { revision: root.revision, replay: null };
  const replay = object(root.replay);
  if (
    replay.revision !== root.revision ||
    typeof replay.elapsedMilliseconds !== 'number' ||
    !Number.isFinite(replay.elapsedMilliseconds) ||
    replay.elapsedMilliseconds < 0 ||
    typeof replay.frameCount !== 'number' ||
    typeof replay.frames !== 'string' ||
    typeof replay.limited !== 'boolean' ||
    !Array.isArray(replay.bricks) ||
    replay.bricks.length > 128
  )
    throw new Error('Invalid domino replay');
  const bricks = replay.bricks.map((value: unknown) => {
    const brick = object(value);
    if (
      typeof brick.id !== 'number' ||
      !Number.isInteger(brick.id) ||
      brick.id === 0 ||
      brick.id < -32768 ||
      brick.id > 32767 ||
      typeof brick.stamp !== 'string' ||
      brick.stamp.length > 1024
    )
      throw new Error('Invalid domino identity');
    return { id: brick.id, stamp: brick.stamp };
  });
  if (new Set(bricks.map((b) => b.id)).size !== bricks.length)
    throw new Error('Duplicate domino identity');
  const parsed: DominoReplay = {
    revision: root.revision,
    elapsedMilliseconds: replay.elapsedMilliseconds,
    frameCount: replay.frameCount,
    frames: replay.frames,
    limited: replay.limited,
    bricks,
  };
  decodeDominoFrames(parsed);
  return { revision: root.revision, replay: parsed };
}

export type DominoRequest = (
  since: string,
  action: DominoAction | undefined,
  signal: AbortSignal,
) => Promise<DominoResponse>;

/** Low-frequency revision polling; unchanged responses contain no physics data. */
export class DominoClient {
  private revision = '';
  private readonly abort = new AbortController();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private generation = 0;
  private acting = false;
  private playingUntil = 0;
  constructor(
    private readonly request: DominoRequest,
    private readonly active: () => boolean,
    private readonly apply: (replay: DominoReplay | null) => void,
    private readonly refresh: () => void,
    private readonly status: (message: string) => void,
  ) {
    void this.poll();
  }
  private accept(response: DominoResponse): void {
    if (response.revision === this.revision) return;
    if (response.replay === undefined) throw new Error('Missing domino replay');
    this.playingUntil =
      response.replay === null
        ? 0
        : performance.now() +
          (response.replay.frameCount - 1) * 50 -
          response.replay.elapsedMilliseconds;
    this.apply(response.replay);
    this.revision = response.revision;
    if (response.replay?.limited)
      this.status('This run reached the 45-second preview limit. Reset to try another layout.');
  }
  private async poll(): Promise<void> {
    const generation = this.generation;
    try {
      this.refresh();
      if (this.active() && !this.acting) {
        const response = await this.request(this.revision, undefined, this.abort.signal);
        if (!this.abort.signal.aborted && generation === this.generation) this.accept(response);
      }
    } catch {
      /* Retry transient failures without discarding the last authoritative poses. */
    } finally {
      if (!this.abort.signal.aborted) this.timer = setTimeout(() => void this.poll(), 2000);
    }
  }
  private stopped(): boolean {
    return this.abort.signal.aborted;
  }
  async act(
    action: 'contact' | 'walk' | 'reset',
    buildingId: number,
    contact?: DominoContact,
  ): Promise<void> {
    if (
      this.acting ||
      this.stopped() ||
      (action !== 'reset' && performance.now() < this.playingUntil)
    )
      return;
    this.acting = true;
    const generation = ++this.generation;
    if (action !== 'walk')
      this.status(action === 'contact' ? 'Preparing domino run…' : 'Resetting dominoes…');
    try {
      const response = await this.request(
        '',
        { action, buildingId, ...(contact === undefined ? {} : { contact }) },
        this.abort.signal,
      );
      if (!this.abort.signal.aborted && generation === this.generation) {
        this.status('');
        this.accept(response);
      }
    } catch (error) {
      if (!this.abort.signal.aborted && generation === this.generation)
        this.status(error instanceof Error ? error.message : 'The domino action failed.');
    } finally {
      this.acting = false;
    }
  }
  invalidateLayout(): void {
    ++this.generation;
    this.revision = '';
    this.playingUntil = 0;
    this.apply(null);
  }
  dispose(): void {
    this.abort.abort();
    clearTimeout(this.timer);
  }
}
